<template>
  <div
      ref="scrollContainer"
      class="w-full overflow-x-auto overflow-y-hidden sticky snap-x snap-mandatory lg:snap-none no-scrollbar scroll-smooth"
      @scroll.passive="watchScroll"
      :class="{
        'fixed insert-0 z-50 h-screen w-screen my-auto content-center': popupImage
      }"
  >
    <div v-if="popupImage" class="fixed bottom-6 right-6 z-50 w-10 h-10" @click="$emit('popupImage', false)"
         :aria-label="t(pageStr + '.close')" role="button">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6">
        <path fill-rule="evenodd"
              d="M3.22 3.22a.75.75 0 0 1 1.06 0l3.97 3.97V4.5a.75.75 0 0 1 1.5 0V9a.75.75 0 0 1-.75.75H4.5a.75.75 0 0 1 0-1.5h2.69L3.22 4.28a.75.75 0 0 1 0-1.06Zm17.56 0a.75.75 0 0 1 0 1.06l-3.97 3.97h2.69a.75.75 0 0 1 0 1.5H15a.75.75 0 0 1-.75-.75V4.5a.75.75 0 0 1 1.5 0v2.69l3.97-3.97a.75.75 0 0 1 1.06 0ZM3.75 15a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-2.69l-3.97 3.97a.75.75 0 0 1-1.06-1.06l3.97-3.97H4.5a.75.75 0 0 1-.75-.75Zm10.5 0a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-2.69l3.97 3.97a.75.75 0 1 1-1.06 1.06l-3.97-3.97v2.69a.75.75 0 0 1-1.5 0V15Z"
              clip-rule="evenodd"/>
      </svg>

    </div>
    <ul
        class="grid grid-flow-col auto-cols-max lg:grid-cols-1 xl:grid-cols-2 lg:grid-flow-row lg:auto-cols-auto lg:gap-2 p-0.5 bg-white relative"
    >
      <li id="imageIdx-1" class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
          style="aspect-ratio: 1/1">
        <item-image
            :image-url="image.filename"
            :image-layer-url="image.filename_layer"
            :main-metal="image.main_metal"
            :zoom=true
        />
      </li>
      <li v-if="engraving.text != ''" id="imageIdxEngraving"
          class="w-screen lg:w-full bg-white snap-center lg:snap-align-none">
        <item-engraving :engraving="engraving" :main-metal="image.main_metal"/>
      </li>
      <li
          v-for="(image_pos, image_pos_idx) in image.positions"
          :key="image_pos.filename"
          :id="'imageIdx' + image_pos_idx"
          class="w-screen lg:w-full bg-white snap-center lg:snap-align-none"
          style="aspect-ratio: 1/1"
      >
        <item-image
            :image-url="image_pos.filename"
            :image-layer-url="image_pos.filename_layer"
            :main-metal="image.main_metal"
            :zoom=true
        />
      </li>
      <li v-if="(image.positions.length % 2 != 0 && engraving.text != '') || image.positions.length % 2 == 0"
          class="hidden lg:block">
        <item-v2-item-image-brand-logo :main-metal="image.main_metal" :brand="brandLogoId"/>
      </li>
    </ul>
  </div>
</template>
<script setup lang="ts">
import ItemImage from "@/components/itemImage.vue";
import ItemEngraving from "@/components/itemEngraving.vue";
import {computed, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import ItemV2ItemImageBrandLogo from "@/components/itemV2/itemV2ItemImageBrandLogo.vue";

const {t} = useI18n();
const scrollContainer = ref<HTMLElement | null>(null);
const props = defineProps([
  "image",
  "engraving",
  "imageIdx",
  "popupImage",
  "itemNumber"
]);
const pageStr = "components.itemV2.itemV2ImageGrid"

/**
 * image idx
 */
const emits = defineEmits([
  "switchImage",
  "popupImage"
]);

let isProgrammaticScroll = false;
let timeout: any = null;

const watchScroll = () => {
  if (isProgrammaticScroll || !scrollContainer.value) return;
  getImageInViewport();
}

const brandLogoId = computed(() => {
  return (props.itemNumber.substring(0, 2) == "R-") ? "R" : "S"
})

watch(
    () => props.imageIdx,
    (imageIdx: string | number) => {
      isProgrammaticScroll = true;
      if (timeout) clearTimeout(timeout);

      scrollContainer.value?.querySelector("#imageIdx" + imageIdx)?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest"
      });

      // Reset the flag after the smooth scroll is likely finished
      timeout = setTimeout(() => {
        isProgrammaticScroll = false;
      }, 500);
    },
    {deep: true},
);

const getImageInViewport = () => {

  // main images
  checkImgaeInViewport("imageIdx-1", "-1")

  // engraving

  if (props.engraving.text != '') {
    checkImgaeInViewport("imageIdxEngraving", "Engraving")
  }

  // image positions

  for (let i = 0; i < props.image.positions.length; i++) {
    checkImgaeInViewport("imageIdx" + i, i.toString())
  }
}

const checkImgaeInViewport = (nodeId: string, imagePoxIdx: string) => {
  const el = scrollContainer.value?.querySelector("#" + nodeId) as HTMLElement;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  const parent = scrollContainer.value; // The scroll container
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();

  // Check if the center of the element is within the viewport of the scroll container
  const elementCenter = rect.left + rect.width / 2;
  const parentCenter = parentRect.left + parentRect.width / 2;

  // If the element is mostly visible in the center (allowing some margin)
  if (Math.abs(elementCenter - parentCenter) < rect.width / 2) {
    emits('switchImage', imagePoxIdx)
  }
}

const isSetImageExists = computed(() => {
  if (typeof props.image.set === 'undefined' || typeof props.image.set.filename === 'undefined') return false;
  return props.image.set.filename != ""
})

</script>
<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
